import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import range from 'lodash/range';
import filter from 'lodash/filter';
import { makeStyles } from '@material-ui/styles';
import { ExclamationCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import { Popover } from 'antd';

import { BookingJourney } from '../../constants/booking';

const useStyles = makeStyles(
  theme => ({
    progressWrapper: {
      width: 'fit-content !important',
    },

    popContent: {
      width: 240,
      '& > .ant-popover-content': {
        borderRadius: 2,
        '& > .ant-popover-arrow': {
          right: 22,
          borderRightColor: 'rgba(0, 0, 0, 0.75)',
          borderBottomColor: 'rgba(0, 0, 0, 0.75)',
        },
        '& > .ant-popover-inner': {
          padding: 8,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',

          '& .ant-popover-inner-content': {
            padding: 0,
            color: theme.colors.gray1,
          },
        },
      },
    },

    popTitle: {
      ...theme.typography.body2,
      marginLeft: 8,
    },

    popIcon: {
      color: theme.colors.gray8,

      '& > svg': {
        width: 12,
        height: 12,
      },
    },

    popCompletedIcon: {
      color: theme.colors.green6,
    },

    popItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 4,
      '&:last-child': {
        marginBottom: 0,
      },
    },

    popItemSet: {
      position: 'relative',
      ...theme.typography.body4,
      color: theme.colors.gray6,
      marginLeft: 17,
      cursor: 'pointer',
      '&::before': {
        content: '""',
        height: 12,
        width: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        position: 'absolute',
        left: -8,
        top: 5,
      },
    },

    wrapper: {
      display: 'flex',
      alignItems: 'center',
    },

    progressContainer: ({ step }) => ({
      display: 'grid',
      gridColumnGap: 2,
      gridTemplateColumns: `repeat(${step}, 20px)`,
    }),

    step: {
      height: 6,
      backgroundColor: theme.colors.gray3,
      '&:first-child': {
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
      },
      '&:last-child': {
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
      },
    },

    activeStep: {
      backgroundColor: theme.colors.orange6,
    },

    completedStep: {
      backgroundColor: theme.colors.green11,
    },

    pendingIcon: {
      color: theme.colors.orange6,
      marginLeft: 8,
      marginRight: 4,
      '& > svg': {
        width: 10.5,
        height: 10.5,
      },
    },

    completedIcon: {
      color: theme.colors.green11,
      marginLeft: 8,
      marginRight: 4,

      '& > svg': {
        width: 10.5,
        height: 10.5,
      },
    },

    activeCount: {
      ...theme.typography.body4,
      color: theme.colors.titleColor,
    },

    totalCount: {
      ...theme.typography.body4,
      color: theme.colors.disableColor,
    },
  }),
  { name: 'pendingProgress' },
);

const PendingProgress = ({
  property,
  isCMS,
  contractUrl,
  commissionUrl,
  paymentUrl,
  payoutUrl,
  paymentMethodUrl,
}) => {
  const history = useHistory();
  const {
    publishCondition: { commissionTier, contractStage, paymentPlan, payout, offlinePaymentMethod },
  } = property;

  const isManual = [BookingJourney.MANUAL, BookingJourney.FULL_AUTOMATIC].includes(
    property?.bookingJourney,
  );
  const paymentOnline = !property.paymentPlan || property?.paymentPlan?.paymentOnline;
  const step = isManual ? 2 : 4;
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingAlert, setPendingAlert] = useState();
  const styles = useStyles({ step, pendingCount });

  const handleGotoSet = (e, key) => {
    e.stopPropagation();
    const linkCondition = isCMS ? key === 'Contract' : key === 'Payment Plan';
    const paymentPartUrl =
      !paymentOnline && paymentPlan ? [paymentUrl, paymentMethodUrl] : [paymentUrl, payoutUrl];
    const linkUrl = isCMS ? [contractUrl, commissionUrl] : paymentPartUrl;
    history.push(linkCondition ? linkUrl[0] : linkUrl[1]);
  };

  const handleCalcPendingCount = () => {
    const pendingAlertObject = {
      Contract: contractStage,
      Commission: commissionTier,
    };

    if (!isManual) {
      const alertData =
        !paymentOnline && paymentPlan
          ? { ['Payment Plan']: paymentPlan, ['Payment Method']: offlinePaymentMethod }
          : { ['Payment Plan']: paymentPlan, ['Payout Plan']: payout };
      Object.assign(pendingAlertObject, alertData);
    }

    setPendingCount(filter(pendingAlertObject)?.length);
    setPendingAlert(pendingAlertObject);
  };

  useEffect(() => {
    if (property) {
      handleCalcPendingCount();
    }
  }, [property]);

  const renderPopContent = useMemo(() => {
    const popData =
      !paymentOnline && paymentPlan
        ? ['Payment Plan', 'Payment Method']
        : ['Payment Plan', 'Payout Plan'];
    const setItem = isCMS ? ['Contract', 'Commission'] : popData;
    return (
      <div>
        <For of={Object?.keys(pendingAlert ?? [])} each="item" index="index">
          <div className={styles.popItem} key={index}>
            <CheckCircleFilled
              className={classnames(styles.popIcon, pendingAlert[item] && styles.popCompletedIcon)}
            />
            <span className={styles.popTitle}>{item}</span>
            <If condition={setItem.includes(item) && !pendingAlert[item]}>
              <span onClick={e => handleGotoSet(e, item)} className={styles.popItemSet}>
                Go to Set
              </span>
            </If>
          </div>
        </For>
      </div>
    );
  }, [property, pendingAlert]);

  // TODO: antd升级5.x后，可以进行替换
  return (
    <div className={styles.progressWrapper}>
      <Popover content={renderPopContent} overlayClassName={styles.popContent} placement="topRight">
        <div className={styles.wrapper}>
          <div className={styles.progressContainer}>
            <For of={range(step)} each="item" index="index">
              <div
                key={index}
                className={classnames(
                  styles.step,
                  index + 1 <= pendingCount && step !== pendingCount && styles.activeStep,
                  step === pendingCount && styles.completedStep,
                )}
              />
            </For>
          </div>

          {pendingCount !== step ? (
            <ExclamationCircleFilled className={styles.pendingIcon} />
          ) : (
            <CheckCircleFilled className={styles.completedIcon} />
          )}
          <div>
            <span className={styles.activeCount}>{pendingCount}</span>
            <span className={styles.totalCount}>/{step}</span>
          </div>
        </div>
      </Popover>
    </div>
  );
};

PendingProgress.propTypes = {
  property: PropTypes.object.isRequired,
  contractUrl: PropTypes.string,
  commissionUrl: PropTypes.string,
  paymentUrl: PropTypes.string,
  payoutUrl: PropTypes.string,
  isCMS: PropTypes.bool,
};

PendingProgress.defaultProps = {
  contractUrl: '/',
  commissionUrl: '/',
  paymentUrl: '/',
  payoutUrl: '/',
  isCMS: true,
};

export default PendingProgress;
